import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  ComposedChart,
  ReferenceLine,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts'
import { convertTimestampToTime, pSBC } from './../utils'
import { stateDisplays, periods, predictWindows, clusterColors, locationColors } from '../constants'
import Loading from './Loading'

const CustomLabel = props => {
  return (
    <g>
      <text
        x={props.viewBox.x + (props.xOffset || 0) + 4}
        y={props.viewBox.y + (props.yOffset || 0)}
        fill="#777"
        style={{fontSize: "14px"}}
      >
        {props.label}
      </text>
    </g>
  )
}

function Chart({
  width = 600,
  height = 400,
  setpoint = null,
  max = null,
  type = "line",
  colors = {default: "#fdaa48"},
  channels,
  records,
  events,
  predicts,
  group,
  postfixY
}) {
  const [groups, setGroups] = useState([])
  const [data, setData] = useState([])

  useEffect(() => {
    let ngroups = new Set()
    channels.forEach(c => {
      ngroups.add(c[group]);
    })
    ngroups = Array.from(ngroups)
    setGroups(ngroups)
  }, [group, channels])

  useEffect(() => {
    if (!records.time) return;
    let ndata = []
    records.time.forEach(i => {
      let elem = { time: i }
      groups.forEach(g => {
        const elems = channels
          .map(c => c.name in records && c[group] === g ? records[c.name][i] : undefined)
          .filter(e => e !== undefined)
        const sum =  elems.reduce((a, b) => a + b, 0)
        elem[g.toUpperCase()] = parseFloat(((sum / elems.length) || 0.0).toFixed(2))
      })
      ndata.push(elem)
    })
    if (predicts.time) {
      if (ndata.length > 0) {
        const last = Object.keys(ndata[ndata.length-1]).map(k => [k !== "time" ? k + "_p" : k, ndata[ndata.length-1][k]])
        ndata.push(Object.fromEntries(new Map(last)))
      }
      predicts.time.forEach(i => {
        let elem = { time: i }
        groups.forEach(g => {
          const elems = channels
            .map(c => c.name in predicts && c[group] === g ? predicts[c.name][i] : undefined)
            .filter(e => e !== undefined)
          const sum =  elems.reduce((a, b) => a + b, 0)
          elem[g.toUpperCase() + "_p"] = parseFloat(((sum / elems.length) || 0.0).toFixed(2))
        })
        ndata.push(elem)
      })
    }
    setData(ndata)
  }, [records, predicts, group, groups, channels])

  const eventLines = Object.keys(events).map((t, i) =>
    <ReferenceLine
      x={t}
      key={t}
      stroke="#6699cc"
      strokeDasharray="3 2"
      label={<CustomLabel
      yOffset={i * 14}
      label={stateDisplays[events[t]]}/>}
    />
  )

  const predictLines = groups.map((g, i) => (
    <Line
      key={g + "_p"}
      type="monotone"
      dataKey={g.toUpperCase() + "_p"}
      stroke={colors[g] || pSBC(i / groups.length * 0.5, colors.default)}
      dot={false}
      strokeDasharray="3 3"
    />
  ))

  const predictAreas = groups.map(g => (
    <Area
      key={g + "_p"}
      type="monotone"
      dataKey={g.toUpperCase() + "_p"}
      stroke={colors[g] || colors.default}
      fillOpacity={.5}
      fill={"url(#color" + g + ")"}
      dot={false}
      strokeDasharray="3 3"
    />
  ))

  return (
    <ResponsiveContainer width="100%" height={height}>
    {type === "area" ?
      <ComposedChart width={width} height={height} data={data} margin={{ top: 15, right: 10, left: -25, bottom: 5 }} >
        <defs>
        {groups.map(g => (
          <linearGradient key={g} id={"color" + g} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={colors[g] || colors.default} stopOpacity={0.8}/>
            <stop offset="95%" stopColor={colors[g] || colors.default} stopOpacity={0}/>
          </linearGradient>
        ))}
        </defs>
        <XAxis
          tick={{ fontSize: 10, fill: "#6699cc" }}
          dataKey="time" scale="time" type="number"
          domain={['dataMin', 'dataMax']}
          tickFormatter={tick => convertTimestampToTime(tick, false)}
        />
        <YAxis
          tick={{ fontSize: 10, fill: "#6699cc" }}
          tickFormatter={tick => tick + postfixY}
          domain={['auto', 'auto']}
          allowDecimals={false}
        />
        <Tooltip labelStyle={{ fontSize: 10 }} itemStyle={{ fontSize: 10 }} labelFormatter={convertTimestampToTime} />
        <CartesianGrid stroke="#f5f5f5" />
        {groups.map(g => (
          <Area
            key={g}
            type="monotone"
            dataKey={g.toUpperCase()}
            stroke={colors[g] || colors.default}
            fillOpacity={1}
            fill={"url(#color" + g + ")"}
          />
        ))}
        {predictAreas}
        {setpoint !== null && <ReferenceLine y={setpoint} stroke="#bcbcbc" strokeDasharray="3 3" />}
        {max !== null && <ReferenceLine y={max} stroke="#ff474c" strokeDasharray="3 3" />}
        {eventLines}
      </ComposedChart> :
      <ComposedChart width={width} height={height} data={data} margin={{ top: 15, right: 10, left: -25, bottom: 5 }} >
        <XAxis
          tick={{ fontSize: 10, fill: "#6699cc" }}
          dataKey="time" scale="time" type="number"
          domain={['dataMin', 'dataMax']}
          tickFormatter={tick => convertTimestampToTime(tick, false)}
        />
        <YAxis
          tick={{ fontSize: 10, fill: "#6699cc" }}
          tickFormatter={tick => tick + postfixY}
          domain={['auto', 'auto']}
          allowDecimals={false}
        />
        <Tooltip labelStyle={{ fontSize: 10 }} itemStyle={{ fontSize: 10 }} labelFormatter={convertTimestampToTime} />
        <CartesianGrid />
        {groups.map((g, i) => (
          <Line
            key={g}
            type="monotone"
            dataKey={g.toUpperCase()}
            stroke={colors[g] || pSBC(i / groups.length * 0.5, colors.default)}
            dot={false}
            strokeWidth={2}
          />
        ))}
        {predictLines}
        {setpoint !== null && <ReferenceLine y={setpoint} stroke="#bcbcbc" strokeDasharray="3 3"/>}
        {max !== null && <ReferenceLine y={max} stroke="#ff474c" strokeDasharray="3 3"/>}
        {eventLines}
      </ComposedChart>}
    </ResponsiveContainer>
  )
}

function ChartTab({onClick, activeTab, label}) {
  return (
    <li className="nav-item" onClick={() => {
      onClick(label);
    }}>
      <a href={"#" + label} className={"nav-link" + (activeTab === label ? " active" : "")}>
        <span className="d-md-block d-none text-muted">{label}</span>
        <span className="d-md-none d-block text-muted mb-0 text text-xs">{label}</span>
      </a>
    </li>
  )
}

export default function ReportChart({user, facility, component, report}) {
  const [channels, setChannels] = useState([])
  const [records, setRecords] = useState({time:[]})
  const [events, setEvents] = useState({time:[]})
  const [predicts, setPredicts] = useState({time:[]})
  const [activeTab, setActiveTab] = useState('Mean')
  const [period, setPeriod] = useState('Max')
  const [predictWindow, setPredictWindow] = useState('30 min')
  const [isLoaded, setIsLoaded] = useState(false)
  const endTime = Math.floor(report.end_time)
  const hasPredict = user && user.permissions.includes("predict")

  useEffect(() => {
    setIsLoaded(false)
  }, [report.name])

  useEffect(() => {
    const fetchReport = async (tag, endTime) => {
      try {
        const rsp = await axios.get('/api/v1/report/' + tag, { params: {
          type: component.type,
          start: endTime - periods[period][0],
          resample: periods[period][1]
        }})
        const keys = Object.keys(rsp.data.records).filter(c => c !== "time")
        const ch = rsp.data.channels.split(",")
        const cl = rsp.data.clusters.split(",")
        const po = rsp.data.positions.split(",")
        setChannels(ch.map((c, i) => ({
          name: c.toLowerCase(),
          cluster: cl[i],
          position: po[i],
          component: component.name
        })).filter(c => keys.includes(c.name)))
        setEvents(rsp.data.events)
        setRecords(rsp.data.records)
        setIsLoaded(true)

      } catch (error) {
        setChannels([])
        setRecords({time:[]})
        setEvents({})
        setIsLoaded(true)
      }
    }
    fetchReport(report.name, endTime)
  }, [period, component.name, component.type, report.name, endTime])

  useEffect(() => {
    const fetchPredict = async () => {
      try {
        const rsp = await axios.get('/api/v1/predict/' + facility.id, { params: {
          resample: periods[period][1],
          start: endTime,
          end: endTime + predictWindows[predictWindow],
        }})
        setPredicts(rsp.data.result)
      } catch(error) {
        setPredicts({time:[]})
      }
    }
    if (hasPredict) {
      fetchPredict()
    }
  }, [period, predictWindow, endTime])

  const props = {
    events: events,
    setpoint: component.min_value,
    max: component.max_value,
    records: records,
    channels: channels,
    predicts: predicts,
    postfixY: "°C"
  }

  const render = (height) => {
    switch (activeTab) {
      default: return <Chart height={height} group="component" type="area" {...props} />
      case "Cluster": return <Chart height={height} group="cluster" colors={clusterColors} {...props} />
      case "Position": return <Chart height={height} group="position" colors={locationColors} {...props} />
      case "All": return <Chart height={height} group="name" {...props} />
    }
  }

  return (
    <>
      <div className="card-header bg-transparent">
        <div className="row align-items-center">
          <div className="col">
            <h5 className="card-title text-uppercase text-muted mb-0">{facility.name}</h5>
            <span className="h2 font-weight-bold mb-0">{report && report.name}</span>
          </div>
          <div className="col-sm-auto ml-auto">
            <ul className="nav nav-tabs justify-content-end">
            {['Mean', 'Cluster', 'Position', 'All'].map((label) =>
              <ChartTab
                activeTab={activeTab}
                key={label}
                label={label}
                onClick={() => setActiveTab(label)}
              />
            )}
            </ul>
          </div>
        </div>
      </div>
      <div className="d-flex flex-column card-body pt-0 pb-1">
        <div className="tab-content" style={{maxHeight: "30em"}}>
          {isLoaded ?
          <>
            <div className="d-md-block d-none">
              {render(480)}
            </div>
            <div className="d-md-none d-block">
              {render(300)}
            </div>
          </>:
          <Loading/>}
        </div>
        <div className="row">
          <div className="col-7">
            {hasPredict && <ul className="nav nav-pills justify-content-start mb-1">
              <span className="pr-2">Forecast:</span>
              {Object.entries(predictWindows).map(([label, tab]) =>
                <li key={label} className="nav-item mr-2 mr-md-0 pr-1">
                  <button
                   className={"nav-link py-1 px-2" + (label === predictWindow ? " active" : "")}
                   style={{cursor: "pointer"}}
                   onClick={() => setPredictWindow(label)}
                  >
                    <span className="d-md-block d-none">{label}</span>
                    <span className="d-md-none d-block text text-xs">{label}</span>
                  </button>
                </li>
              )}
            </ul>}
          </div>
          <div className="col-5">
            <ul className="nav nav-pills justify-content-end mb-1">
              {Object.entries(periods).map(([label, tab]) =>
                <li key={label} className="nav-item mr-2 mr-md-0 pr-1">
                  <button
                   className={"nav-link py-1 px-2" + (label === period ? " active" : "")}
                   style={{cursor: "pointer"}}
                   onClick={() => setPeriod(label)}
                  >
                    <span className="d-md-block d-none">{label}</span>
                    <span className="d-md-none d-block text text-xs">{label}</span>
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </>
  )
}